import React from 'react';
import { ArrowLeft, ArrowRight, Wand2, AlertCircle, ListOrdered } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import Stepper from '../Stepper';
import WizardFrame from './WizardFrame';

/**
 * Wizard step 2 of 4: whatever's left to configure once participant type,
 * count, and (for teams) roster size are already known — currently that's
 * only ladder rounds, for formats declaring `supportsLadderRounds`. Legs
 * (round_robin, cross_group, page_playoff) is NOT configured here — it
 * always starts at its default of 1 and is only ever adjusted afterward,
 * from a Legs control on the Result screen (see GeneratedScheduleStep.jsx
 * and useScheduleBuilder's `generate` overrides param) — regenerating is
 * cheap and this avoids asking upfront for a value most organizers leave at
 * the default anyway. Participant type/count/roster are ALWAYS already set
 * by the time this step is reached (Test Data in standalone mode, or the
 * real tournament's own enrollment/type/roster in tournament mode) — this
 * step never re-asks for any of them, only the step's own Next button label
 * depends on the format: it reads "Set Groups" when a Groups step follows,
 * or "Generate Schedule" when this is the last step before generating (see
 * ScheduleBuilderTab.jsx / useScheduleBuilder's `needsGroupsStep`).
 */
const ConfigureParametersStep = ({ format, config, updateConfig, needsGroupsStep, backToFormat, proceedFromParameters, eligibleFormats, error }) => {
  const { theme } = useTheme();
  const { participantCount, ladderRounds } = config;
  // Same map as the Format step (formatEligibility.js) — guards against
  // this screen being reached with a selection that's since become invalid
  // (count or sport changed via Back), not just a fresh pick.
  const { enabled, reason: ineligibleReason } = eligibleFormats[format.id];
  const belowMin = !enabled;

  // A fixed-2-sides format (Dual Meet) with exactly 2 teams has nothing to
  // group afterward — those 2 teams already ARE the two competing sides.
  const skipsGroups = format.fixedGroupCount && participantCount === format.fixedGroupCount;

  return (
    <WizardFrame
      title="Configure parameters"
      footer={
        <>
          <button
            type="button"
            onClick={backToFormat}
            className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${theme.buttonGhost}`}
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <div className="flex flex-col items-end">
            <button
              type="button"
              onClick={proceedFromParameters}
              disabled={belowMin}
              className={`flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-all ${theme.shadow} ${
                belowMin ? `${theme.buttonGhost} cursor-not-allowed opacity-50` : `${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99]`
              }`}
            >
              {needsGroupsStep ? <ArrowRight size={16} /> : <Wand2 size={16} />}
              {needsGroupsStep ? 'Set Groups' : 'Generate Schedule'}
            </button>
            {belowMin && ineligibleReason && (
              <p className={`mt-1.5 text-right text-xs ${theme.textMuted}`}>{ineligibleReason}</p>
            )}
          </div>
        </>
      }
    >
      {format.supportsLadderRounds && (
        <div className={`rounded-xl border ${theme.border} ${theme.backgroundSecondary} p-4`}>
          <div className={`mb-3 flex items-center gap-2 text-sm font-semibold ${theme.text}`}>
            <ListOrdered size={16} />
            Ladder length
          </div>
          <Stepper
            label="Number of ladder rounds"
            hint="Only Round 1 (each court's round robin) is generated now — courts reshuffle by promotion/relegation as results come in, for however many rounds you run."
            value={ladderRounds}
            min={1}
            max={20}
            onChange={(v) => updateConfig({ ladderRounds: v, ladderRoundsUserAdjusted: true })}
          />
        </div>
      )}

      {skipsGroups && !belowMin && (
        <p className={`mt-4 text-xs ${theme.textMuted}`}>
          Exactly {format.fixedGroupCount} teams — they're already the two competing sides, no group setup needed next.
        </p>
      )}

      {error && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">
          <AlertCircle size={15} />
          {error}
        </div>
      )}
    </WizardFrame>
  );
};

export default ConfigureParametersStep;
